import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SessionProvider } from './context/SessionContext';
import Chatbot from './components/Chatbot';
import ConversationSidebar from './components/ConversationSidebar';
import Login from './pages/Login';
import Register from './pages/Register';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import './App.css';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-[#f8f5fa] via-[#faf8fc] to-[#f5f0f8]">
        <div className="flex flex-col items-center gap-3">
          <img 
            src="static/kal-cropped.svg" 
            alt="Kal Logo" 
            className="w-[150px] h-auto animate-pulse" 
          />
          <div className="w-8 h-8 border-[3px] border-kaleido-purple/20 border-t-kaleido-purple rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public route - redirects to home if already authenticated
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-[#f8f5fa] via-[#faf8fc] to-[#f5f0f8]">
        <div className="flex flex-col items-center gap-3">
          <img 
            src="static/kal-cropped.svg" 
            alt="Kal Logo" 
            className="w-[150px] h-auto animate-pulse" 
          />
          <div className="w-8 h-8 border-[3px] border-kaleido-purple/20 border-t-kaleido-purple rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }
  
  return children;
}

// Main chat layout with integrated sidebar
function ChatLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <SessionProvider>
      {/* App container - mobile: flex column, desktop: grid */}
      {/* Using h-dvh for dynamic viewport height on mobile (accounts for iOS address bar) */}
      <div className="flex flex-col lg:grid lg:grid-cols-[minmax(18rem,20vw)_1fr] h-dvh w-full max-w-[100vw] overflow-hidden">
        
        {/* Mobile overlay - only visible when sidebar open on mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/30 z-[999] animate-fadeIn" 
            onClick={toggleSidebar} 
          />
        )}
        
        {/* Left sidebar with branding AND conversation history */}
        <div className={`
          sidebar-divider
          fixed top-0 left-0 w-[85vw] max-w-[340px] h-dvh z-[1000]
          bg-gradient-to-br from-[rgba(248,245,250,0.98)] to-[rgba(245,240,248,0.98)]
          backdrop-blur-[10px] shadow-[2px_0_20px_rgba(0,0,0,0.15)]
          py-3 pl-3 pr-4 pt-[max(1.5rem,env(safe-area-inset-top))]
          pb-[max(0.75rem,env(safe-area-inset-bottom))]
          flex flex-col overflow-hidden
          transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:relative lg:translate-x-0 lg:w-auto lg:z-auto
          lg:pt-2.5 lg:pl-2.5 lg:pr-2.5 lg:pb-2
          lg:bg-gradient-to-br lg:from-[rgba(152,96,167,0.06)] lg:to-[rgba(38,58,149,0.045)]
          lg:backdrop-blur-none lg:shadow-none
          lg:h-full
        `}>
          <header className="flex flex-col h-full min-h-0">
            {/* Logo section */}
            <div className="shrink-0">
              <img 
                src="static/kal-cropped.svg" 
                alt="Kal Logo" 
                className="
                  w-[75px] max-w-[50%] h-auto
                  lg:ml-1 lg:mt-1
                  lg:w-[clamp(10rem,13vw,15.5rem)] lg:max-w-[54%]
                  drop-shadow-[0_0.125rem_0.5rem_rgba(152,96,167,0.15)]
                  transition-transform duration-300 hover:scale-105
                " 
              />
            </div>

            {/* Conversation history - integrated into sidebar */}
            <ConversationSidebar onToggle={toggleSidebar} />
          </header>
          
          {/* Mobile close button */}
          <button 
            className="
              lg:hidden
              flex absolute top-2 right-2 p-1
              bg-transparent border-none rounded-sm cursor-pointer
              text-kaleido-gray-500 z-10
              hover:bg-kaleido-purple/10 hover:text-kaleido-gray-700
            " 
            onClick={toggleSidebar}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Main content area */}
        <div className="flex flex-col flex-1 min-w-0 h-dvh overflow-hidden relative">
          {/* Kaleido branding - desktop (bottom right) */}
          <div className="
            hidden lg:flex
            absolute bottom-3 right-3 flex-col items-end
            opacity-45 hover:opacity-75 transition-opacity duration-200 z-10
          ">
            <span className="text-[0.5em] mb-0.5 text-kaleido-gray-500">powered by</span>
            <img 
              src="static/asset_1b.png" 
              alt="Kaleido Logo" 
              className="h-[1rem] w-auto" 
            />
          </div>

          {/* Mobile header with menu toggle - hidden on desktop.
              Floats above the chat so messages can scroll underneath. */}
          <div className="
            lg:hidden
            absolute top-0 left-0 right-0 z-40
            flex items-center pl-2 pr-3 py-1 pt-[max(1rem,env(safe-area-inset-top))]
            pointer-events-none
          ">
            {/* Left: hamburger button (matches selector pill height) */}
            <div className="flex items-center shrink-0 pointer-events-auto">
              <button
                className="
                  flex items-center justify-center h-[40px] w-[40px]
                  bg-white/40 backdrop-blur-sm rounded-full cursor-pointer
                  border border-kaleido-purple/15 shadow-sm
                  text-kaleido-gray-700/80 hover:bg-white/70 hover:text-kaleido-gray-700
                "
                onClick={toggleSidebar}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              </button>
            </div>
            {/* Right: selector icons grouped into a single translucent pill */}
            <div className="flex items-center justify-end flex-1 ml-2 pointer-events-auto">
              <div className="
                flex items-center gap-[10px] px-[10px] h-[40px]
                bg-white/40 backdrop-blur-sm rounded-full
                border border-kaleido-purple/15 shadow-sm
              ">
                <div id="mobile-model-selector" className="flex items-center"></div>
                <div id="mobile-reasoning-selector" className="flex items-center"></div>
                <div id="mobile-prompt-selector" className="flex items-center"></div>
              </div>
            </div>
          </div>

          {/* Desktop glassmorphic toolbar portal target - absolute overlay spanning full width */}
          <div id="desktop-toolbar-portal" className="hidden lg:block absolute top-0 left-0 right-0 z-40"></div>

          {/* Main chat content */}
          <main className="
            flex justify-center items-stretch flex-1 min-h-0 overflow-hidden box-border
            px-[max(0.25rem,env(safe-area-inset-left))]
            pr-[max(0.25rem,env(safe-area-inset-right))]
            pb-0
            pt-1
            sm:px-[max(0.5rem,env(safe-area-inset-left))]
            sm:pr-[max(0.5rem,env(safe-area-inset-right))]
            sm:pt-2
            lg:p-[clamp(1rem,3vw,2rem)] lg:pb-0
          ">
            <div className="w-full flex flex-col h-full min-h-0 overflow-hidden">
              <Chatbot />
            </div>
          </main>
        </div>
      </div>
    </SessionProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <ChatLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/:tab"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
